import { useQuery, useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/auth'

/**
 * Bookmarks — starred conversations, stored in Supabase `bookmarks`.
 *
 * Public shape preserved from the pre-TQ version so call sites don't change:
 *   { bookmarkedUrns, loading, error, toggleBookmark, isBookmarked }
 *
 * Realtime postgres_changes → invalidateQueries keeps multiple tabs / devices
 * in sync with zero app-level plumbing.
 */
export const bookmarksKey = (userId: string | undefined): QueryKey => ['bookmarks', userId ?? 'anon']

export function useBookmarks() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const key = bookmarksKey(user?.id)

  const query = useQuery<Set<string>>({
    queryKey: key,
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase.from('bookmarks').select('conversation_urn')
      if (error) throw new Error(error.message)
      return new Set((data ?? []).map((b) => b.conversation_urn))
    },
  })

  // Realtime invalidation is handled centrally by <RealtimeBridge />.

  const toggle = useMutation({
    mutationKey: ['bookmarks', 'toggle'],
    mutationFn: async ({ conversationUrn, action }: { conversationUrn: string; action: 'add' | 'remove' }) => {
      if (!user) throw new Error('Not authenticated')
      if (action === 'remove') {
        const { error } = await supabase
          .from('bookmarks')
          .delete()
          .eq('user_id', user.id)
          .eq('conversation_urn', conversationUrn)
        if (error) throw new Error(error.message)
      } else {
        const { error } = await supabase
          .from('bookmarks')
          .insert({ user_id: user.id, conversation_urn: conversationUrn })
        if (error) throw new Error(error.message)
      }
      return { urn: conversationUrn, action }
    },
    // Optimistic toggle: flip the cache in onMutate so a rapid second click
    // reads the already-toggled state and correctly dispatches the opposite
    // action. Without this, two clicks within the ~100ms round-trip both
    // read "not bookmarked", both INSERT, and the second fails with a
    // unique-constraint violation — or worse, both read "bookmarked",
    // both DELETE, and the Supabase insert never happened in the first
    // place leaving the DB out of sync with the cache.
    onMutate: async ({ conversationUrn, action }) => {
      await qc.cancelQueries({ queryKey: key })
      const snapshot = qc.getQueryData<Set<string>>(key) ?? new Set<string>()
      qc.setQueryData<Set<string>>(key, (prev) => {
        const next = new Set(prev ?? [])
        if (action === 'add') next.add(conversationUrn)
        else next.delete(conversationUrn)
        return next
      })
      return { snapshot }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot) qc.setQueryData<Set<string>>(key, ctx.snapshot)
    },
    // No onSuccess cache mutation — onMutate already applied it. Realtime
    // invalidation will confirm / overwrite if a different tab raced us.
  })

  const bookmarkedUrns = query.data ?? new Set<string>()

  return {
    bookmarkedUrns,
    loading: !!user && query.isPending,
    error: query.error instanceof Error ? query.error.message : null,
    // The caller's click handler reads the *current* cache (already reflects
    // any in-flight optimistic toggle) to decide direction. Rapid double-
    // clicks therefore dispatch opposite actions and the final DB state
    // matches the final cache state.
    toggleBookmark: (conversationUrn: string) => {
      const current = qc.getQueryData<Set<string>>(key) ?? new Set<string>()
      const action: 'add' | 'remove' = current.has(conversationUrn) ? 'remove' : 'add'
      return toggle.mutateAsync({ conversationUrn, action })
    },
    isBookmarked: (conversationUrn: string) => bookmarkedUrns.has(conversationUrn),
  }
}
